import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CircleNotch, SealCheck } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminApi, getErrorMessage } from "@/lib/api";
import { aud, btnPrimary, selectCls } from "../ui";
import { Blockers, ClaimFacts, Duplicates } from "./ClaimFacts";
import { ApprovePlan } from "./ApprovePlan";
import { H } from "./parts";

const inputCls = `${selectCls} w-full`;

function PartsChoice({ value, onChange, suggestion }) {
  const opts = [[true, "Parts needed from OTM"], [false, "No parts needed"]];
  return (
    <div data-testid="wi-approve-parts-choice">
      <H>Parts</H>
      <div className="flex bg-black/40 border border-neutral-800 rounded-full p-0.5 w-fit">
        {opts.map(([v, l]) => (
          <button key={l} type="button" onClick={() => onChange(v)} data-testid={`wi-approve-parts-${v ? "yes" : "no"}`}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${value === v ? "bg-[#73bf44] text-[#050505] font-semibold" : "text-neutral-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      <p className="text-[0.7rem] text-neutral-500 mt-1.5" data-testid="wi-approve-parts-suggestion">Suggested: {suggestion.needs_parts ? "parts needed" : "no parts"} · {suggestion.reason}</p>
    </div>
  );
}

export function ApproveForm({ p, onDone }) {
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ amount: p.claim.quoted_total ?? "", approved_by: "", rectification: "", needs_parts: p.parts.needs_parts, dup: false });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { adminApi.get("/admin/wi/monday-users").then(({ data }) => setUsers(data.users)).catch(() => {}); }, []);
  const amount = Number(f.amount);
  const ready = !p.blockers.length && f.amount !== "" && amount >= 0 && f.approved_by && (!p.duplicates.length || f.dup);
  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post(`/admin/wi/approvals/${p.claim.id}`, { amount, approved_by: f.approved_by, rectification: f.rectification, needs_parts: f.needs_parts, confirm_not_duplicate: f.dup }, { timeout: 120000 });
      data.status === "done" ? toast.success(`Approved as ${data.otma}`) : toast.error(data.error || "The approval stopped part-way. You can retry it.");
      onDone(data);
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="wi-approve-form">
      <ClaimFacts p={p} />
      <Blockers items={p.blockers} />
      <Duplicates items={p.duplicates} checked={f.dup} onChange={set("dup")} blocked={p.blockers.length > 0} />
      {!p.blockers.length && (
        <>
          <PartsChoice value={f.needs_parts} onChange={set("needs_parts")} suggestion={p.parts} />
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block"><H>Approved amount (inc GST)</H>
              <input type="number" min="0" step="0.01" value={f.amount} onChange={(e) => set("amount")(e.target.value)} className={inputCls} data-testid="wi-approve-amount" /></label>
            <label className="block"><H>Approved by</H>
              <select value={f.approved_by} onChange={(e) => set("approved_by")(e.target.value)} className={inputCls} data-testid="wi-approve-approver">
                <option value="">Choose who approved it…</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select></label>
          </div>
          <label className="block"><H>Approved work · goes on the register and in the email</H>
            <textarea rows={3} maxLength={1900} value={f.rectification} onChange={(e) => set("rectification")(e.target.value)} className={inputCls}
              placeholder="e.g. Replace kitchen sink and seal. Send parts to RV Centre WA workshop" data-testid="wi-approve-rectification" /></label>
          <ApprovePlan p={p} needsParts={f.needs_parts} />
          <ConfirmButton p={p} f={f} amount={amount} ready={ready} busy={busy} onConfirm={approve} />
        </>
      )}
    </div>
  );
}

function ConfirmButton({ p, f, amount, ready, busy, onConfirm }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" className={btnPrimary} disabled={!ready || busy} data-testid="wi-approve-submit">
          {busy ? <><CircleNotch size={15} className="animate-spin" /> Writing to Monday.com…</> : <><SealCheck size={16} weight="fill" /> Approve as {p.next_otma}</>}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#0F0F0F] border-neutral-800 text-neutral-200" data-testid="wi-approve-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">Approve {p.claim.chassis} for {aud(amount, 2)}?</AlertDialogTitle>
          <AlertDialogDescription className="text-neutral-400">
            This writes to Monday.com straight away: approval number {p.next_otma}, claim moved to {f.needs_parts ? "Needs Parts" : "Approved"}, and a new row on the Warranty Claims register. The emails are saved as drafts only.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-transparent border-neutral-700 text-neutral-300" data-testid="wi-approve-confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="bg-[#73bf44] text-[#050505] hover:bg-[#5fa338]" data-testid="wi-approve-confirm-ok">Approve and update Monday.com</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
